import type { ReactElement } from 'react';
import { Menu, MenuButton, MenuItem, MenuList, Button } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLanguage } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';

import { getGlobalConfig } from '../../../utilities/globalConfig';

// Renders a Chakra menu of the per-uni supported languages. Hidden when the
// uni only supports one language so single-language tenants see no UI change.
//
// `i18n.changeLanguage` triggers re-render of every consumer of `useTranslation`
// and the language detector caches the choice to localStorage('i18n.language')
// so the next page load resumes in the same language.
export const LanguagePicker = (): ReactElement | null => {
  const { i18n, t } = useTranslation();

  const cfg = safeGlobalConfig();
  const languages = Array.isArray(cfg?.languages) && cfg.languages.length > 0 ? cfg.languages : ['en'];

  if (languages.length <= 1) return null;

  const current = i18n.resolvedLanguage ?? i18n.language;

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        size="sm"
        leftIcon={<FontAwesomeIcon icon={faLanguage} />}
        aria-label={t('common:language')}
      >
        {labelFor(current)}
      </MenuButton>
      <MenuList>
        {languages.map(lang => (
          <MenuItem key={lang} onClick={() => void i18n.changeLanguage(lang)}>
            {labelFor(lang)}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

function labelFor(tag: string): string {
  try {
    const dn = new Intl.DisplayNames([tag], { type: 'language' });
    return dn.of(tag) ?? tag;
  } catch {
    return tag;
  }
}

function safeGlobalConfig(): ReturnType<typeof getGlobalConfig> | undefined {
  try {
    return getGlobalConfig();
  } catch {
    return undefined;
  }
}
